import { currentLanguage, type Language, LOCALE_TAG } from '@/i18n';

type Formatters = { date: Intl.DateTimeFormat; time: Intl.DateTimeFormat };

const cache = new Map<Language, Formatters>();

/** Date and time formatters for the current UI language (built once per language). */
function formatters(): Formatters {
  const lang = currentLanguage();
  let f = cache.get(lang);
  if (!f) {
    const tag = LOCALE_TAG[lang];
    f = {
      date: new Intl.DateTimeFormat(tag, { year: 'numeric', month: '2-digit', day: '2-digit' }),
      time: new Intl.DateTimeFormat(tag, { hour: '2-digit', minute: '2-digit' }),
    };
    cache.set(lang, f);
  }
  return f;
}

export function formatDate(iso: string | null | undefined): string {
  return iso ? formatters().date.format(new Date(iso)) : '—';
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const f = formatters();
  return `${f.date.format(d)} ${f.time.format(d)}`;
}

export function isToday(iso: string): boolean {
  return new Date(iso).toDateString() === new Date().toDateString();
}

/** Weekday names in the UI language, ISO order: index 0 = Monday … 6 = Sunday. */
export function weekdayNames(style: 'long' | 'short' = 'long'): string[] {
  const f = new Intl.DateTimeFormat(LOCALE_TAG[currentLanguage()], { weekday: style });
  // 1 January 2024 was a Monday.
  return Array.from({ length: 7 }, (_, i) => f.format(new Date(2024, 0, 1 + i)));
}

/** A whole-dinar amount in the UI language's grouping ("4 500 RSD"). */
export function formatRsd(value: number): string {
  return `${new Intl.NumberFormat(LOCALE_TAG[currentLanguage()]).format(value)} RSD`;
}
